import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { TWclassNames } from "../Div";

/**
 * RightDrawer — generic slide-in panel (right by default, or `side="left"`).
 *
 * Used as the row "detail" pull-out for the Promang Table View.
 *
 *   <RightDrawer open={open} onClose={close} eyebrow="Sak #123" title="Tittel"
 *     footer={<...>} actions={[{ label, onClick, primary }]} side="left">
 *     ...body...
 *   </RightDrawer>
 */
export const RightDrawer = ({
  open,
  onClose,
  eyebrow,
  title,
  actions = [],
  footer,
  width = 460,
  side = "right",
  /** Phone-width: sheet fills the viewport (not a skinny desktop rail). */
  fullBleedMobile = false,
  /** Optional controls in the header (e.g. upload), left of the close button. */
  headerExtra,
  children,
}) => {
  const fromLeft = side === "left";
  return (
    <Transition show={!!open} as={Fragment}>
      <Dialog as="div" className="relative z-[300]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div
            className={`absolute inset-y-0 flex max-w-full ${
              fromLeft ? "left-0" : "right-0"
            } ${fullBleedMobile ? "max-md:inset-0 max-md:max-w-none" : ""}`}
          >
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-[cubic-bezier(.2,.8,.2,1)] duration-300"
              enterFrom={fromLeft ? "-translate-x-full" : "translate-x-full"}
              enterTo="translate-x-0"
              leave="transform transition ease-in duration-200"
              leaveFrom="translate-x-0"
              leaveTo={fromLeft ? "-translate-x-full" : "translate-x-full"}
            >
              <Dialog.Panel
                className={TWclassNames(
                  "flex h-full flex-col bg-white shadow-[0_16px_44px_rgba(0,0,0,0.20),0_4px_12px_rgba(0,0,0,0.10)]",
                  fullBleedMobile &&
                    "max-md:!h-[100dvh] max-md:!w-full max-md:!max-w-none max-md:pt-[env(safe-area-inset-top)] max-md:pb-[env(safe-area-inset-bottom)] max-md:pl-[env(safe-area-inset-left)] max-md:pr-[env(safe-area-inset-right)]"
                )}
                style={{ width, maxWidth: "92vw" }}
              >
                {/* Header */}
                <div className="flex flex-shrink-0 items-start gap-3 border-b border-slate-200 px-6 py-4">
                  <div className="min-w-0 flex-1">
                    {eyebrow && (
                      <div className="text-[13px] font-semibold tabular-nums text-blue-600">
                        {eyebrow}
                      </div>
                    )}
                    <Dialog.Title className="mt-0.5 text-[18px] font-bold leading-tight tracking-tight text-slate-900">
                      {title}
                    </Dialog.Title>
                  </div>
                  {headerExtra ? (
                    <div className="flex flex-shrink-0 items-center gap-2">{headerExtra}</div>
                  ) : null}
                  <button
                    type="button"
                    onClick={onClose}
                    className={TWclassNames(
                      "grid flex-shrink-0 place-items-center rounded-[9px] text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700",
                      fullBleedMobile ? "h-11 w-11 max-md:h-11 max-md:w-11" : "h-[34px] w-[34px]"
                    )}
                  >
                    <XMarkIcon className="h-[18px] w-[18px]" />
                  </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

                {/* Footer / actions — actions kept on the left for easy reach */}
                {(footer || actions.length > 0) && (
                  <div className="flex flex-shrink-0 items-center gap-2 border-t border-slate-200 px-6 py-3.5">
                    <div className="flex items-center gap-2">
                      {actions.map((a, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={a.onClick}
                          className={TWclassNames(
                            "inline-flex items-center gap-2 rounded-[10px] px-4 text-[14px] font-medium transition-colors",
                            fullBleedMobile ? "h-11 min-h-11" : "h-[38px]",
                            a.primary
                              ? "bg-slate-900 text-white hover:bg-slate-700"
                              : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                          )}
                        >
                          {a.icon && <a.icon className="h-[16px] w-[16px]" />}
                          {a.label}
                        </button>
                      ))}
                    </div>
                    {footer && <div className="ml-auto">{footer}</div>}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

/** Definition-list row used inside drawer bodies. */
export const DrawerProps = ({ items = [] }) => (
  <dl className="grid grid-cols-[120px_1fr] items-center gap-x-4 gap-y-3.5">
    {items
      .filter((it) => it && it.label)
      .map((it, i) => (
        <Fragment key={i}>
          <dt className="text-[13px] font-medium text-slate-400">{it.label}</dt>
          <dd className="text-[14px] text-slate-700">{it.value ?? "—"}</dd>
        </Fragment>
      ))}
  </dl>
);

/** Section wrapper with an uppercase label, matching the design's drawer sections. */
export const DrawerSection = ({ title, children }) => (
  <div className="mt-6 first:mt-0">
    {title && (
      <h4 className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">
        {title}
      </h4>
    )}
    {children}
  </div>
);

export default RightDrawer;
